/**
 * [INPUT]: 依赖 react；依赖 react-router 的 Links/Meta/Outlet/Scripts/ScrollRestoration；依赖 ../src/index.css (Tailwind)
 * [OUTPUT]: 对外提供 Layout（HTML 外壳：静态 head 资产 + <Meta/> + <Links/> + {children}）、默认 App（<Outlet/> 子路由出口）、HydrateFallback
 * [POS]: apps/web/app 的 RR7 框架模式根路由；取代旧 index.html 的 HTML 外壳职责
 *   - 路由级 head（title/description/robots/canonical/og 与 twitter 系列）由各 route 的 meta export 经 <Meta/> 注入
 *   - 跨路由静态资产（favicon/manifest/字体/Plausible/og:image 尺寸）在此处固化
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import { Links, Meta, Outlet, Scripts, ScrollRestoration, type LinksFunction } from 'react-router'
import stylesheet from '../src/index.css?url'

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: stylesheet },
]

const PLAUSIBLE_INIT = `window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};
plausible.init()`

/** HTML 外壳：静态 head 资产 + 路由 <Meta/>/<Links/>，body 为路由出口 + 滚动恢复 + 客户端脚本。 */
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#0f172a" />
        <meta name="color-scheme" content="dark" />

        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />

        {/* 跨路由固定的社交图片元数据（og:image 本身由 route meta 注入，此处只放尺寸/alt 常量） */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image:alt" content="PSL Analyzer — free on-device PSL facial attractiveness score" />

        {/* Privacy-friendly analytics by Plausible */}
        <script async src="https://ga.tagmybeat.com/js/pa-nFzwSV2I3mRdqPgUptd5d.js" />
        <script dangerouslySetInnerHTML={{ __html: PLAUSIBLE_INIT }} />

        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

/** 预渲染 HTML 已含完整内容；HydrateFallback 仅在路由模块加载完成前的极短窗口使用。 */
export function HydrateFallback() {
  return null
}

/** 根路由组件：渲染子路由出口，被 Layout 包裹注入 HTML 外壳。 */
export default function App() {
  return <Outlet />
}